// src/view/ui/ViewBusyController.ts
/**
 * Контроллер состояния занятости (busy) для View.
 *
 * Отвечает за:
 *  - глобальный спиннер в пределах view,
 *  - локальный loader на кнопке-источнике (с задержкой),
 *  - блокировку элементов с .cmd-disabled,
 *  - синхронизацию состояния командной панели (если есть).
 *
 * Контроллер не хранит режим редактирования, он берёт его через getMode(),
 * если вы передадите колбэк в конструктор. Иначе будет использовать "view".
 */
class ViewBusyController {
    /**
     * @param view    экземпляр IViewWidget (корневой API view)
     */
    constructor(view) {
        /** ID активного таймера для кнопки-лоадера */
        this.btnLoaderID = null;
        this.view = view;
    }
    /**
     * Устанавливает режим занятости.
     * Если передана кнопка, на ней появится локальный loader (с задержкой 1s),
     * иначе — включается глобальный спиннер для всего view.
     */
    modeBusy(btn) {
        const host = this.view.element;
        const uiNotMask = this.view.getUiNotMask?.() ?? "";
        // Локальный loader на кнопке (если это «обычная» кнопка, а не cmdbutton и т.п.)
        if (btn &&
            !btn.classList.contains("link-button") &&
            btn.tagName !== "INPUT" &&
            btn.tagName !== "TEXTAREA" &&
            !btn.classList.contains("btn-unset") &&
            !btn.classList.contains("im-cmdbutton")) {
            this.btnLoaderID = window.setTimeout(() => {
                btn.classList.add("btn-loading");
            }, 1000);
        }
        else {
            // Глобальный busy
            host.classList.add("im-view-busy");
            const loadingOff = host.getAttribute("data-loadingcontainer") === "off";
            const hasLoading = host.querySelector(`.loading-container:not(.im-table .loading-container)${uiNotMask}`);
            if (!hasLoading && !loadingOff) {
                host.insertAdjacentHTML("afterbegin", `<div class="loading-container simple"><span class="loading ui-corner-all"></span></div>`);
            }
            host
                .querySelectorAll(`.loading-container:not(.im-table .loading-container)${uiNotMask}`)
                .forEach(el => el.classList.add("on"));
        }
        // Блокируем элементы с .cmd-disabled
        host.querySelectorAll(".cmd-disabled").forEach(el => {
            el.classList.add("off");
            if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
                el.disabled = true;
            }
        });
        this.updatePanelState();
    }
    /**
     * Снимает режим занятости.
     * Если передана кнопка, у неё снимается локальный loader и чистится таймер.
     */
    modeNormal(btn) {
        const host = this.view.element;
        const uiNotMask = this.view.getUiNotMask?.() ?? "";
        if (btn) {
            if (this.btnLoaderID !== null) {
                clearTimeout(this.btnLoaderID);
                this.btnLoaderID = null;
            }
            btn.classList.remove("btn-loading");
        }
        host.classList.remove("im-view-busy");
        host
            .querySelectorAll(`.loading-container:not(.im-table .loading-container)${uiNotMask}`)
            .forEach(el => el.classList.remove("on"));
        // Разблокируем .cmd-disabled
        host.querySelectorAll(".cmd-disabled").forEach(el => {
            el.classList.remove("off");
            if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
                el.disabled = false;
            }
        });
        this.view.dom.applyViewMode();
        this.updatePanelState();
    }
    /**
     * Обновляет состояние командной панели (если подключена).
     * Режим вычисляется как:
     *  - "loading", если view находится в busy-состоянии,
     *  - иначе — значение из getMode() или "view".
     */
    updatePanelState() {
        const panel = this.view.cmdPanel;
        if (!panel)
            return;
        const host = this.view.element;
        const uiNotMask = this.view.getUiNotMask?.() ?? "";
        const isBusy = !!host.querySelector(`.loading-container:not(.im-table .loading-container).on${uiNotMask}`) ||
            host.classList.contains("im-view-busy");
        const mode = isBusy ? "loading" : this.view.mode ?? "view";
        panel.setState({
            mode,
            hasCurrent: true,
            selectedCount: 0,
            filterOn: false
        });
    }
}

export { ViewBusyController };
